import { useState, useEffect, useCallback, useContext } from "react";
import { getLocalInfo } from "../services/locals.services";
import { BUTTONS } from "../components/Desktop/Tabla";
import SnackBarUtils from "../../../utils/SnackBarUtils";
import {
  changeRubroProducts,
  deleteProducts,
  pauseProducts,
  unPauseProducts,
} from "../services/products.services";
import { ProductosVendedorContext } from "../context/productos.vendedor.context";
import { IProducto } from "../../AdminProducts/types/producto";
import { ProductosAdminContext } from "../../AdminProducts/context/productos.context";
import { TenantsContext } from "../../../context/tenants.context";

export interface AccionesMasivasProps {
  isAdmin: boolean;
  productosSeleccionados: any[];
  setProductosSeleccionados: (p: any[]) => void;
  setConfirmDialog: (confirm: any) => void;
}

const useAccionesMasivas = ({
  isAdmin,
  productosSeleccionados,
  setProductosSeleccionados,
  setConfirmDialog,
}: AccionesMasivasProps) => {
  const { estado, getProductosVendedor } = useContext(ProductosVendedorContext);
  const { fetchData } = useContext(ProductosAdminContext);
  const [loadingProductosMasivos, setLoadingProductosMasivos] =
    useState<boolean>(false);
  const {tenant} = useContext(TenantsContext);

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false,
    }));
  };

  const fetchProducts = async () => {
    if (isAdmin) {
      return await fetchData();
    } else {
      return await getProductosVendedor(estado.key);
    }
  };

  const handleDeleteProducts = useCallback(() => {
    setConfirmDialog({
      open: true,
      text: {
        title: "¿Deseas eliminar estas publicaciones?",
        body: "Ten en cuenta que esto no se puede deshacer",
        leftButton: "Cancelar",
        rightButton: "Confirmar",
        colorButtonConfirm: "error",
      },
      disabled: loadingProductosMasivos,
      action: async () => {
        setLoadingProductosMasivos(true);
        try {
          const ids: string = productosSeleccionados
            .map((p: any) => (isAdmin ? p.idProducto : p.idproducto))
            .join(",");
          if (ids.length === 0)
            return SnackBarUtils.error("No hay productos seleccionados");
          await deleteProducts(ids, tenant);
          await fetchProducts();
          SnackBarUtils.success("Productos eliminados");
          setProductosSeleccionados([]);
          clearConfirmDialog();
        } catch (e) {
          console.log(e);
        } finally {
          setLoadingProductosMasivos(false);
        }
      },
      close: () => clearConfirmDialog(),
    });
  }, [productosSeleccionados, getProductosVendedor, loadingProductosMasivos]);

  const handlePauseProducts = useCallback(() => {
    setConfirmDialog({
      open: true,
      text: {
        title: "¿Deseas pausar estas publicaciones?",
        body: "",
        leftButton: "Cancelar",
        rightButton: "Confirmar",
      },
      disabled: loadingProductosMasivos,
      action: async () => {
        setLoadingProductosMasivos(true);
        try {
          const ids: string = productosSeleccionados
            .map((p: any) => (isAdmin ? p.idProducto : p.idproducto))
            .join(",");
          if (ids.length === 0)
            return SnackBarUtils.error("No hay productos seleccionados");

          await pauseProducts(ids, tenant);
          await fetchProducts();
          SnackBarUtils.success("Productos pausados");
          setProductosSeleccionados([]);
          clearConfirmDialog();
        } catch (e) {
          console.log(e);
        } finally {
          setLoadingProductosMasivos(false);
        }
      },
      close: () => clearConfirmDialog(),
    });
  }, [productosSeleccionados, getProductosVendedor, loadingProductosMasivos]);

  const handleUnPauseProducts = useCallback(() => {
    setConfirmDialog({
      open: true,
      text: {
        title: "¿Deseas reanudar estas publicaciones?",
        body: "",
        leftButton: "Cancelar",
        rightButton: "Confirmar",
      },
      disabled: loadingProductosMasivos,
      action: async () => {
        setLoadingProductosMasivos(true);
        try {
          const ids: string = productosSeleccionados
            .map((p: any) => (isAdmin ? p.idProducto : p.idproducto))
            .join(",");
          if (ids.length === 0)
            return SnackBarUtils.error("No hay productos seleccionados");

          await unPauseProducts(ids, tenant);
          await fetchProducts();
          SnackBarUtils.success("Productos reanudados");
          setProductosSeleccionados([]);
          clearConfirmDialog();
        } catch (e) {
          console.log(e);
        } finally {
          setLoadingProductosMasivos(false);
        }
      },
      close: () => clearConfirmDialog(),
    });
  }, [productosSeleccionados, getProductosVendedor, loadingProductosMasivos]);

  const handleChangeRubro = useCallback(
    async (idRubro: number) => {
      setLoadingProductosMasivos(true);
      try {
        let ids: number[] = [];
        productosSeleccionados.map((p: IProducto) =>
          ids.push(p.idProducto)
        );
        if (ids.length === 0)
          return SnackBarUtils.error("No hay productos seleccionados");

        await changeRubroProducts(ids, idRubro, tenant);
        await fetchData();
        SnackBarUtils.success("Rubros actualizados");
        setProductosSeleccionados([]);
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingProductosMasivos(false);
      }
    },
    [productosSeleccionados, getProductosVendedor, loadingProductosMasivos]
  );

  return {
    loadingProductosMasivos,
    handleDeleteProducts,
    handlePauseProducts,
    handleUnPauseProducts,
    handleChangeRubro,
  };
};

export default useAccionesMasivas;
